// Кадрите на станционния канал като чисти функции „списък → списък" —
// `useStationWebSocket` ги прилага два пъти: на живо, щом кадърът дойде, и
// ПОВТОРНО върху снимката (`initial_data` / `refresh_data`), която го е
// изпреварила. Без React, за да се тестват и двата пътя поотделно.
//
// Защо повторно (docs/monitors.md §WebSocket слой): сървърът — облакът И
// касата — записва сокета за push-ове ПРЕДИ да построи снимката. Поръчка,
// записана, докато снимката се строи, стига до екрана като `new_order`, а
// после пристига снимката, прочетена отпреди нея, и я заличава. Обратният ред
// на сървъра не помага: тогава push-ът между снимката и записа изобщо не стига
// до сокета. Затова клиентът пази кадрите, дошли преди снимката, и ги прилага
// пак върху нея.
import type { BillSplit, Order, WebSocketMessage } from "../pages/monitors/types";
import { getRowId, isSameOrder } from "../pages/monitors/types";

/** Кадрите, които снимката може да изпревари — всичко освен самата снимка. */
const INCREMENTAL_TYPES: ReadonlySet<WebSocketMessage["type"]> = new Set([
  "new_order",
  "status_update",
  "item_cancelled",
  "bill_split",
]);

export const isSnapshotMessage = (message: WebSocketMessage): boolean =>
  message.type === "initial_data" || message.type === "refresh_data";

export const isIncrementalMessage = (message: WebSocketMessage): boolean =>
  INCREMENTAL_TYPES.has(message.type);

/** `new_order` ЗАМЕСТВА поръчка със същата самоличност, не се долепя: една и
 * съща поръчка стига дотук два пъти по няколко пътя — касата печата
 * кухненския билет ПРЕДИ да извести мониторите (документът вече е в store-а,
 * значи монитор, свързал се в този прозорец, я взима и в `initial_data`, и
 * като `new_order`), реконект, replay след връщане на облака. Долепянето
 * правеше две картички, а екранът ги сливаше в една със СЪБРАНИ количества. */
export const applyNewOrder = (previous: Order[], newOrder: Order): Order[] => {
  const index = previous.findIndex((order) => isSameOrder(order, newOrder));
  if (index === -1) return [newOrder, ...previous];
  const next = [...previous];
  // Свежият кадър бие стария — носи същите редове с актуални статуси; мястото
  // в списъка остава, за да не подскача екранът.
  next[index] = newOrder;
  return next;
};

export const applyStatusUpdate = (
  previous: Order[],
  updatedOrder: Order,
): Order[] =>
  previous.map((order) => {
    if (
      order.billId !== updatedOrder.billId ||
      order.billElementId !== updatedOrder.billElementId
    ) {
      return order;
    }
    // Запазваме оригиналната подредба на елементите. Обновяваме само статуса
    // на съответните елементи. Матчваме по уникалния ид на реда (getRowId),
    // защото един и същ продукт може да е на няколко отделни реда в поръчката
    // — itemId сам по себе си е двусмислен. Fallback към itemId за стари
    // payload-и без ид.
    const updatedElements = order.elements.map((element) => {
      const elementRowId = getRowId(element);
      const matchingElement = updatedOrder.elements.find((updatedElement) => {
        const updatedRowId = getRowId(updatedElement);
        if (elementRowId != null && updatedRowId != null) {
          return (
            Boolean(updatedElement.isAddition) === Boolean(element.isAddition) &&
            updatedRowId === elementRowId
          );
        }
        return updatedElement.itemId === element.itemId;
      });
      if (!matchingElement) return element;
      // Продукт на няколко станции носи и общия статус на реда (`rowStatus`) —
      // шпикерът показва него; без копирането шпикерът би останал на стария
      // общ статус. На ред с една станция ключовете ги няма → нищо не се пипа.
      return {
        ...element,
        status: matchingElement.status,
        ...(matchingElement.rowStatus !== undefined
          ? { rowStatus: matchingElement.rowStatus }
          : {}),
        ...(matchingElement.stationIds !== undefined
          ? { stationIds: matchingElement.stationIds }
          : {}),
        // Частите по станции — иначе „+ Студена кухня · готово" стои на стария
        // статус до „refresh".
        ...(matchingElement.stationStatuses !== undefined
          ? { stationStatuses: matchingElement.stationStatuses }
          : {}),
      };
    });
    return { ...order, elements: updatedElements };
  });

/** Слива поръчки с еднакви `billId` и `billElementId` в една: бекендът праща
 * отделни Order обекти за активните и за сторнираните продукти на поръчката. */
const mergeSameOrders = (orders: Order[]): Order[] =>
  orders.reduce((merged, order) => {
    const existing = merged.find(
      (candidate) =>
        candidate.billId === order.billId &&
        candidate.billElementId === order.billElementId,
    );
    if (existing) {
      existing.elements = [...existing.elements, ...order.elements];
    } else {
      merged.push({ ...order });
    }
    return merged;
  }, [] as Order[]);

export const applyItemCancelled = (
  previous: Order[],
  data: Order | Order[],
): Order[] => {
  const mergedCancelledOrders = mergeSameOrders(
    Array.isArray(data) ? data : [data],
  );
  // Заменяме старите поръчки с обединените — сторното не добавя поръчка.
  return previous.map(
    (order) =>
      mergedCancelledOrders.find(
        (cancelled) =>
          cancelled.billId === order.billId &&
          cancelled.billElementId === order.billElementId,
      ) ?? order,
  );
};

export const applyBillSplit = (previous: Order[], split: BillSplit): Order[] => {
  let next = [...previous];
  // Премахваме старите поръчки с oldBillId
  if (split.oldBill.length > 0) {
    const oldBillId = split.oldBill[0].billId;
    next = next.filter((order) => order.billId !== oldBillId);
  }
  // Премахваме съществуващи поръчки с newBillId (ако split-ваме в съществуваща маса)
  if (split.newBill.length > 0) {
    const newBillId = split.newBill[0].billId;
    next = next.filter((order) => order.billId !== newBillId);
  }
  return [
    ...mergeSameOrders(split.oldBill),
    ...mergeSameOrders(split.newBill),
    ...next,
  ];
};

/** Един инкрементален кадър върху списъка; непознат тип не пипа нищо. */
export const applyIncremental = (
  previous: Order[],
  message: WebSocketMessage,
): Order[] => {
  switch (message.type) {
    case "new_order":
      return applyNewOrder(previous, message.data as Order);
    case "status_update":
      return applyStatusUpdate(previous, message.data as Order);
    case "item_cancelled":
      return applyItemCancelled(previous, message.data as Order | Order[]);
    case "bill_split":
      return applyBillSplit(previous, message.data as BillSplit);
    default:
      return previous;
  }
};

/** Снимката плюс кадрите, които я изпревариха, в реда на пристигане.
 *
 * `new_order` тук само ДОБАВЯ липсваща поръчка: щом снимката я съдържа, тя е
 * прочетена след създаването ѝ и е поне толкова свежа — заместването би
 * върнало стария кадър. Останалите кадри се прилагат както на живо: те
 * задават стойност (статус, сторно, разделяне), а не трупат, затова
 * повторното прилагане на вече отразен кадър е безвредно. */
export const replayOverSnapshot = (
  snapshot: Order[],
  missed: WebSocketMessage[],
): Order[] =>
  missed.reduce((orders, message) => {
    if (message.type !== "new_order") return applyIncremental(orders, message);
    const newOrder = message.data as Order;
    return orders.some((order) => isSameOrder(order, newOrder))
      ? orders
      : [newOrder, ...orders];
  }, snapshot);
